import { useEffect, useRef, useState, RefObject } from 'react';

interface UseVisibleElementParam {
  containerRef: RefObject<HTMLDivElement>;
  isVisible: boolean;
}

/**
 * @description detect when the element is visible in the viewport
 * @tutorial https://dev.to/producthackers/intersection-observer-using-react-49ko
 * @param { number } threshold - between 0 and 1, indicate at what percentage the element is considered visible
 * @returns { {containerRef: RefObject<HTMLDivElement>, isVisible: boolean} } containerRef: targets the element to be monitored - isVisible: True if the element is visible in the viewport
 */
export const useVisibleElement = (threshold = 1): UseVisibleElementParam => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = containerRef.current;

    const checkVisibility = (entries: IntersectionObserverEntry[]): void => {
      const [entry] = entries;
      setIsVisible(entry.isIntersecting);
    };

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: threshold,
    };

    const observer = new IntersectionObserver(checkVisibility, options);

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [containerRef, threshold]);

  return { containerRef: containerRef, isVisible: isVisible };
};
